// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { advisoryApi as advisoryApiV2 } from '@shared/data-access/api/brokerage/advisory-api.v2';
import type { AdvisoryProposalContractResponseBody } from '@shared/data-access/interfaces/api/brokerage/client-types';
import type { Advisor } from '@shared/data-access/types/brokerage-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSilverAlphaProposals } from '../../hooks/use-silver-alpha-proposals';

const isActiveStatus = (status: string) => status === 'active';
const isPendingStatus = (status: string) => status === 'pending';

const pickFeaturedPending = (
  proposals: AdvisoryProposalContractResponseBody[],
): AdvisoryProposalContractResponseBody | null => {
  const pending = proposals.find(proposal => isPendingStatus(proposal.status));

  if (pending) {
    return pending;
  }

  return proposals[0] ?? null;
};

export const useSilverAlphaHome = () => {
  const { t } = useTranslation();
  const { proposals } = useSilverAlphaProposals();
  const { data: advisors = [] } = advisoryApiV2.useGetAdvisoryUsersQuery();
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<number | null>(null);

  const advisorOptions = useMemo(
    () =>
      advisors.map(advisor => ({
        value: String(advisor.id),
        label: advisor.name?.trim() || advisor.email,
      })),
    [advisors],
  );

  useEffect(() => {
    if (selectedAdvisorId !== null) {
      return;
    }

    const advisorWithProposal = advisors.find(advisor =>
      proposals.some(proposal => proposal.user_id === advisor.id),
    );

    if (advisorWithProposal) {
      setSelectedAdvisorId(advisorWithProposal.id);
      return;
    }

    if (advisors[0]) {
      setSelectedAdvisorId(advisors[0].id);
    }
  }, [advisors, proposals, selectedAdvisorId]);

  const filteredProposals = useMemo(() => {
    if (selectedAdvisorId === null) {
      return proposals;
    }

    return proposals.filter(proposal => proposal.user_id === selectedAdvisorId);
  }, [proposals, selectedAdvisorId]);

  const selectedAdvisor = useMemo<Advisor | null>(
    () => advisors.find(advisor => advisor.id === selectedAdvisorId) ?? null,
    [advisors, selectedAdvisorId],
  );

  const activeProposal = useMemo(
    () =>
      filteredProposals.find(proposal => isActiveStatus(proposal.status)) ?? null,
    [filteredProposals],
  );

  const hasActiveProposal = activeProposal !== null;

  const featuredPendingProposal = useMemo(() => {
    if (hasActiveProposal) {
      return null;
    }

    return pickFeaturedPending(filteredProposals);
  }, [filteredProposals, hasActiveProposal]);

  const otherProposals = useMemo(() => {
    if (hasActiveProposal && activeProposal) {
      return filteredProposals.filter(
        proposal => proposal.id !== activeProposal.id,
      );
    }

    if (featuredPendingProposal) {
      return filteredProposals.filter(
        proposal => proposal.id !== featuredPendingProposal.id,
      );
    }

    return [];
  }, [
    activeProposal,
    featuredPendingProposal,
    filteredProposals,
    hasActiveProposal,
  ]);

  const onAdvisorChangeValue = useCallback((value: string) => {
    const nextId = Number(value);

    if (Number.isFinite(nextId)) {
      setSelectedAdvisorId(nextId);
    }
  }, []);

  return {
    advisorOptions,
    selectedAdvisorId:
      selectedAdvisorId === null ? undefined : String(selectedAdvisorId),
    selectedAdvisor,
    onAdvisorChangeValue,
    showAdvisorSelector: advisorOptions.length > 0,
    advisorDefaultText: t('silver-alpha-home.advisor-placeholder', {
      ns: 'components',
    }),
    hasActiveProposal,
    activeProposal,
    featuredPendingProposal,
    otherProposals,
    hasProposals: filteredProposals.length > 0,
  };
};
